// biome-ignore-all lint/style/useImportType: <tdb>
// biome-ignore-all lint/style/noRestrictedImports: questo sono i file da importare
import { Button } from 'antd';
import * as H from 'history';
import { useCallback } from 'react';
import {
  ExtractRouteParams,
  generatePath,
  Redirect,
  RedirectProps,
  Route,
  RouteProps,
  useHistory,
} from 'react-router';
import { Link, LinkProps } from 'react-router-dom';

type RoutePathsToObj<T extends string> = {
  [K in T]: never;
};

/**
 * Da sovrascrivere nei singoli progetti, es:
 *
 * declare module '@framework/ui' {
 *  export interface BrandedRouterPathsInterface extends RoutePathsToObj<RoutePaths> {}
 * }
 */
export interface BrandedRouterPathsInterface {}

export type BrandedRouterPaths = keyof BrandedRouterPathsInterface;

interface TypedRouteProps extends RouteProps {
  path?: BrandedRouterPaths;
}

interface TypedRedirectProps extends RedirectProps {
  from?: BrandedRouterPaths;
  to: BrandedRouterPaths | TypedLocationDescriptor;
}

type TypedLocationDescriptor = H.LocationDescriptorObject & {
  pathname?: BrandedRouterPaths;
};

type TypedLocationTo =
  | BrandedRouterPaths
  | TypedLocationDescriptor
  | BrandedPath;

interface TypedLinkProps extends LinkProps {
  /**
   * È possibile passare un oggetto con la proprietà pathname o direttamente
   * un path tra quelli censiti.
   * Per gli URL dinamici, usare `generatePath` per generare il path.
   */
  to: TypedLocationTo;
  disabled?: boolean | null;
}

export type BrandedPath = BrandedRouterPaths & { __typedPath: true };

/**
 * Tipizzazione forte per la route.
 */
export function TypedRoute(props: TypedRouteProps) {
  return <Route {...props} />;
}

export function TypedRedirect(props: TypedRedirectProps) {
  return <Redirect {...props} />;
}

export const typedGeneratePath = <T extends BrandedRouterPaths>(
  path: T,
  params?: ExtractRouteParams<T, string>,
  queryString?: string,
) => (generatePath(path, params as any) + (queryString ?? '')) as BrandedPath;

/**
 * Href assoluto comprensivo del basename del router (es. `/frontoffice`),
 * utilizzabile come `href` nativo. Si ottiene solo da `createHref`/`useHref`.
 */
export type BrandedHref = string & { __typedHref: true };

/**
 * Path accettati dalle API href: nelle app è la union delle route registrate;
 * nei contesti in cui la union è vuota (package senza `routes.d.ts`, bundle
 * backend) degrada a `string`, delegando la correttezza all'app che monta il
 * componente.
 */
type HrefRoutePath = [BrandedRouterPaths] extends [never]
  ? string
  : BrandedRouterPaths;

/**
 * Route posseduta da un'altra app, non censita nella union del compile
 * corrente. Si ottiene solo da `foreignRoute`.
 */
export type ForeignRoutePath = string & { __foreignRoute: true };

/**
 * Marca una route di un'altra app per le API href: da usare nei package
 * condivisi compilati anche da app che non registrano quella route.
 */
const typedForeignRoute = (path: string) => path as ForeignRoutePath;

/**
 * Parametri della route: liberi per le route di altre app (`foreignRoute`),
 * estratti dal pattern per le route censite.
 */
type TypedHrefParams<T> = [T] extends [ForeignRoutePath]
  ? { [param: string]: string }
  : ExtractRouteParams<T & string, string>;

/**
 * Crea l'href assoluto di una route tramite `history.createHref` (lo stesso
 * meccanismo di `<Link>`). Da usare per ogni link che non passa dal router
 * (`href` nativo, `window.open`): il solo `generatePath` perde il basename.
 */
const typedCreateHref = <T extends HrefRoutePath | ForeignRoutePath>(
  history: Pick<H.History, 'createHref'>,
  path: T,
  params?: TypedHrefParams<T>,
  queryString?: string,
) =>
  history.createHref({
    // Cast della firma: con union delle route vuota il generico degraderebbe
    // a `never` e nessun path sarebbe accettato.
    pathname: (
      typedGeneratePath as (
        path: string,
        params?: unknown,
        queryString?: string,
      ) => string
    )(path, params, queryString),
  }) as BrandedHref;

/**
 * Hook che restituisce un generatore di href assoluti, per costruire href
 * in liste, callback o rami condizionali.
 */
function useTypedHrefGenerator() {
  const history = useHistory();
  return useCallback(
    <T extends HrefRoutePath | ForeignRoutePath>(
      path: T,
      params?: TypedHrefParams<T>,
      queryString?: string,
    ) => typedCreateHref(history, path, params, queryString),
    [history],
  );
}

type TypedHrefGenerator = ReturnType<typeof useTypedHrefGenerator>;

/**
 * Hook che restituisce l'href assoluto della route indicata. Per la
 * navigazione interna preferire `<Link>`/`history.push`.
 */
function useTypedHref<T extends HrefRoutePath | ForeignRoutePath>(
  path: T,
  params?: TypedHrefParams<T>,
  queryString?: string,
): BrandedHref {
  return useTypedHrefGenerator()(path, params, queryString);
}

export function TypedLink(props: TypedLinkProps) {
  if (props.disabled) {
    return (
      <Button disabled={props.disabled} type="link">
        {props.children}
      </Button>
    );
  }
  return <Link {...props} />;
}

const typedUseHistory = useHistory as <TState>(this: void) => Omit<
  H.History<TState>,
  'push' | 'location' | 'replace'
> & {
  location: H.Location<TState> & {
    pathname: BrandedRouterPaths | BrandedPath;
  };
  push(location: TypedLocationTo, state?: TState): void;
  replace(location: TypedLocationTo, state?: TState): void;
};

export type {
  TypedHrefGenerator as HrefGenerator,
  TypedLocationDescriptor as LocationDescriptor,
  TypedLocationTo as LocationTo,
};
export {
  TypedLink as Link,
  TypedRedirect as Redirect,
  TypedRoute as Route,
  typedCreateHref as createHref,
  typedForeignRoute as foreignRoute,
  typedGeneratePath as generatePath,
  typedUseHistory as useHistory,
  useTypedHref as useHref,
  useTypedHrefGenerator as useHrefGenerator,
};

/**
 * Crea una versione tipizzata di Route, Redirect e Link in base
 * ai path passati.
 */
export function getTypedReactRouter() {
  return {
    Route: TypedRoute,
    Redirect: TypedRedirect,
    Link: TypedLink,
    generatePath: typedGeneratePath,
    useHistory: typedUseHistory,
  };
}
